import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import GlobalSearchApi from '../../api/GlobalSearchApi';
import { highlightText } from './globalSearchUi';
import './GlobalSearch.css';

function sortRecords(group, records, sort) {
  const list = [...(records || [])];
  if (sort !== 'az') return list;
  const titleField = group.columns?.[0]?.fieldName;
  return list.sort((a, b) => {
    const an = String((titleField && a.values?.[titleField]) || a.id || '');
    const bn = String((titleField && b.values?.[titleField]) || b.id || '');
    return an.localeCompare(bn);
  });
}

export default function GlobalSearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = (searchParams.get('q') || '').trim();
  const objectParam = (searchParams.get('object') || '').trim();
  const [draftQ, setDraftQ] = useState(q);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState(objectParam || 'all');
  const [sort, setSort] = useState('rel');

  useEffect(() => {
    setDraftQ(q);
  }, [q]);

  // Always load all allowed objects so every tab stays available.
  const load = useCallback(async () => {
    if (q.length < 2) {
      setGroups([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const api = new GlobalSearchApi();
      const res = await api.search({ q, limit: 50 });
      if (res?.data?.success) {
        setGroups(res.data.data?.groups || []);
      } else {
        setError(res?.data?.message || 'Search failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    setFilter(objectParam || 'all');
    load();
  }, [load, objectParam]);

  const setObjectFilter = (next) => {
    setFilter(next);
    const params = new URLSearchParams(searchParams);
    if (!next || next === 'all') {
      params.delete('object');
    } else {
      params.set('object', next);
    }
    if (q) params.set('q', q);
    setSearchParams(params, { replace: true });
  };

  const applyQuery = (nextQ) => {
    const trimmed = String(nextQ || '').trim();
    const params = new URLSearchParams();
    if (trimmed) params.set('q', trimmed);
    if (filter && filter !== 'all') params.set('object', filter);
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  const visibleGroups = useMemo(() => {
    let list = groups;
    if (filter !== 'all') {
      list = groups.filter(
        (g) => String(g.objectName).toLowerCase() === filter.toLowerCase()
      );
    }
    if (sort === 'type') {
      list = [...list].sort((a, b) =>
        String(a.pluralLabel || a.objectLabel || a.objectName).localeCompare(
          String(b.pluralLabel || b.objectLabel || b.objectName)
        )
      );
    }
    return list.map((g) => ({
      ...g,
      records: sortRecords(g, g.records, sort),
    }));
  }, [groups, filter, sort]);

  const total = groups.reduce((n, g) => n + (g.records?.length || 0), 0);

  return (
    <div className="gs-results-page">
      <div className="gs-results">
        <div className="gs-results-toolbar">
          <h1 className="gs-results-heading">Search results</h1>
          <p className="gs-results-sub">
            {q.length < 2
              ? 'Type to search every object allowed for global search'
              : `${total} records across ${groups.length} objects for “${q}”`}
          </p>

          <div className="gs-results-bar">
            <label className="gs-results-field">
              <FaSearch className="gs-results-field-icon" aria-hidden="true" />
              <input
                type="search"
                value={draftQ}
                placeholder="Search all records"
                autoComplete="off"
                aria-label="Search query"
                onChange={(e) => setDraftQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyQuery(draftQ);
                  }
                }}
              />
            </label>
            <select
              className="gs-results-sort"
              value={sort}
              aria-label="Sort results"
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="rel">Best Match</option>
              <option value="az">Name A–Z</option>
              <option value="type">Object Type</option>
            </select>
          </div>

          {error ? <Alert variant="danger" className="mb-0 mt-3">{error}</Alert> : null}

          {q.length >= 2 ? (
            <div className="gs-results-tabs" role="tablist" aria-label="Filter by object">
              <button
                type="button"
                className={`gs-chip${filter === 'all' ? ' is-on' : ''}`}
                onClick={() => setObjectFilter('all')}
              >
                All
                <span className="gs-chip-count">{total}</span>
              </button>
              {groups.map((g) => (
                <button
                  key={g.objectName}
                  type="button"
                  className={`gs-chip${filter === g.objectName ? ' is-on' : ''}`}
                  onClick={() => setObjectFilter(g.objectName)}
                >
                  {g.pluralLabel || g.objectLabel}
                  <span className="gs-chip-count">{g.records?.length || 0}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : null}

        {!loading && q.length >= 2 && visibleGroups.length === 0 ? (
          <div className="gs-results-section">
            <div className="gs-results-empty">
              <strong>No records match “{q}”</strong>
              Try a name, email, phone number, or company.
            </div>
          </div>
        ) : null}

        {!loading &&
          visibleGroups.map((g) => {
            const cols = g.columns || [];
            const records = g.records || [];
            return (
              <section key={g.objectName} className="gs-results-section">
                <header className="gs-results-section-head">
                  <strong>{g.pluralLabel || g.objectLabel}</strong>
                  <span className="gs-results-section-count">
                    {records.length} result{records.length === 1 ? '' : 's'}
                  </span>
                </header>
                {records.length === 0 ? (
                  <div className="gs-results-empty">No matching records</div>
                ) : (
                  <div className="table-responsive">
                    <table className="gs-results-table">
                      <thead>
                        <tr>
                          {cols.map((col) => (
                            <th key={col.fieldName}>{col.fieldLabel}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row) => (
                          <tr key={row.id}>
                            {cols.map((col, i) => {
                              const value = row.values?.[col.fieldName] || '';
                              if (i === 0) {
                                return (
                                  <td key={col.fieldName}>
                                    <Link className="gs-results-link" to={row.href}>
                                      {highlightText(value || row.id, q)}
                                    </Link>
                                  </td>
                                );
                              }
                              return (
                                <td key={col.fieldName}>
                                  {highlightText(value, q)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
      </div>
    </div>
  );
}
